import type { AppError } from "./types";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database, Json } from "@/integrations/supabase/types";

function toJson(value: unknown): Json {
  if (value === null) return null;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return value;
  if (Array.isArray(value)) return value.map(toJson);
  if (typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, item]) => item !== undefined)
        .map(([key, item]) => [key, toJson(item)]),
    );
  }
  return String(value);
}

export async function logErrorEvent(appError: AppError): Promise<void> {
  try {
    let supabaseClient: SupabaseClient<Database>;
    if (appError.product === "forkseek") {
      const mod = await import("@/integrations/supabase/seekClient");
      supabaseClient = mod.seekSupabase;
    } else if (appError.product === "forkadmin") {
      const mod = await import("@/integrations/supabase/adminClient");
      supabaseClient = mod.adminSupabase;
    } else {
      const mod = await import("@/integrations/supabase/client");
      supabaseClient = mod.supabase;
    }

    await supabaseClient.from("system_error_events").insert({
      environment: import.meta.env.MODE || "production",
      product: appError.product,
      domain: appError.domain,
      error_family: appError.error_family,
      error_code: appError.error_code,
      severity: appError.severity,
      correlation_id: appError.correlation_id,
      entity_type: appError.entity_type,
      entity_id: appError.entity_id,
      user_title: appError.user_title,
      user_message: appError.user_message,
      technical_message: appError.technical_message,
      retry_allowed: appError.retry_allowed,
      frontend_route: typeof window !== "undefined" ? window.location.pathname : null,
      raw_error_payload: appError.context ? toJson(appError.context) : null,
    });
  } catch {
    console.warn("[ErrorLogger] Failed to persist error event");
  }
}
